import React from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "videojs-hls-quality-selector";
import vi from "video.js/dist/lang/vi.json";
import en from "video.js/dist/lang/en.json";
import ko from "video.js/dist/lang/ko.json";
import {
  PLAYER_EVENT,
  QUALITY_EVENT,
} from "../../constants/video-js-constants";
import { LANGUAGES } from "../../constants/language-constants";
import "videojs-contrib-quality-menu";

const getVideoMetrics = (player) => {
  const time = player.currentTime();
  const duration = player.duration();
  
  const validTime = typeof time === 'number' && !isNaN(time) ? time : 0;
  const validDuration = typeof duration === 'number' && !isNaN(duration) ? duration : 0;
  
  const percentCompleted = validDuration > 0 ? Math.round((validTime / validDuration) * 100) : 0;
  
  return { time: validTime, duration: validDuration, percentCompleted };
};

export const VideoPlayer = ({ props }) => {
  const videoRef = React.useRef(null);
  const playerRef = React.useRef(null);
  const {
    options,
    onReady,
    onPlaybackEvent,
    onQualityEvent,
    showQualitySubTitle,
  } = props;
  const removeQualitySubTitle = (player) => {
    const qualityLabels = player
      .el()
      .querySelectorAll(".vjs-quality-menu-item-sub-label");

    qualityLabels.forEach((label) => {
      label.style.display = "none";
    });
  };
  React.useEffect(() => {
    // Make sure Video.js player is only initialized once
    if (!playerRef.current) {
      // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
      const videoElement = document.createElement("video");
      videoElement.classList.add("video-js", "vjs-big-play-centered");
      videoElement.setAttribute("controls", "");
      videoElement.setAttribute("playsinline", "");
      videoRef.current.appendChild(videoElement);

      const player = (playerRef.current = videojs(videoElement, options, () => {
        videojs.addLanguage(LANGUAGES.VIETNAMESE, vi);
        videojs.addLanguage(LANGUAGES.ENGLISH, en);
        videojs.addLanguage(LANGUAGES.KOREAN, ko);
        player.muted(false);
        onReady && onReady(player);
      }));
      let qualityLevels = player.qualityLevels();

      player.on(PLAYER_EVENT.PLAY, () => {
        onPlaybackEvent && onPlaybackEvent(PLAYER_EVENT.PLAY);
      });

      player.on(PLAYER_EVENT.PAUSE, () => {
        const metrics = getVideoMetrics(playerRef.current);
        onPlaybackEvent && onPlaybackEvent(PLAYER_EVENT.PAUSE, metrics);
      });

      player.on(PLAYER_EVENT.SEEKED, () => {});

      player.on(PLAYER_EVENT.ENDED, () => {
        const metrics = getVideoMetrics(playerRef.current);
        onPlaybackEvent && onPlaybackEvent(PLAYER_EVENT.ENDED, metrics);
      });

      player.on(PLAYER_EVENT.TIMEUPDATE, () => {
        const metrics = getVideoMetrics(playerRef.current);
        onPlaybackEvent && onPlaybackEvent(PLAYER_EVENT.TIMEUPDATE, metrics);
      });

      player.on(PLAYER_EVENT.VOLUMECHANGE, () => {});

      player.on(PLAYER_EVENT.ERROR, () => {});

      player.on(PLAYER_EVENT.FULLSCREENCHANGE, () => {});

      player.on(PLAYER_EVENT.RESIZE, () => {});

      player.on(PLAYER_EVENT.QUALITYCHANGE, () => {});

      player.on(PLAYER_EVENT.LOADEDMETADATA, () => {
        if (!showQualitySubTitle) {
          removeQualitySubTitle(player);
        }
      });
      player.on(PLAYER_EVENT.READY, () => {});
      qualityLevels.on(QUALITY_EVENT.CHANGE, function () {
        onQualityEvent &&
          onQualityEvent(
            QUALITY_EVENT.CHANGE,
            qualityLevels[qualityLevels.selectedIndex]
          );
      });
    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
  }, [
    options,
    videoRef,
    onReady,
    onPlaybackEvent,
    onQualityEvent,
    showQualitySubTitle,
  ]);

  React.useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;
      }
    };
  }, [playerRef]);



  return (
    <div data-vjs-player className="w-full h-full block">
      <div className="w-full h-full block video-container" ref={videoRef} />
    </div>
  );
};

export default VideoPlayer;
